import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { goal } from "@/lib/metrica";
import { SETUP_STEPS, getFlow, saveFlow, nextStepIndex, isSetupEligible } from "@/lib/setupFlow";
import { SetupCard } from "@/components/setup/SetupCard";
import { SetupPromptDialog } from "@/components/setup/SetupPromptDialog";

const lsKey = (user, k) => `penz_setup_${k}_${user.company_id || user.username}`;
const lsGet = (k) => { try { return window.localStorage.getItem(k); } catch (_) { return null; } };
const lsSet = (k, v) => { try { window.localStorage.setItem(k, v); } catch (_) { /* yok say */ } };

// Gerçek 0→5 tamamlanma görüldüğünde bir kez onboarding_completed.
function trackCompletion(user, steps) {
  const done = SETUP_STEPS.filter((s) => steps[s.key]).length;
  const key = lsKey(user, "count");
  const prev = lsGet(key);
  if (done === SETUP_STEPS.length && prev !== null && Number(prev) < done) goal("onboarding_completed");
  lsSet(key, String(done));
}

export default function SetupOnboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const eligible = isSetupEligible(user);
  const [status, setStatus] = useState(null);
  const [flow, setFlowState] = useState(getFlow);
  const [hidden, setHidden] = useState(() => (user ? lsGet(lsKey(user, "hidden")) === "1" : false));

  useEffect(() => {
    if (!eligible) return;
    api.get("/setup/status").then(({ data }) => { setStatus(data); trackCompletion(user, data.steps); }).catch(() => {});
  }, [eligible, user]);

  const setFlow = useCallback((f) => { saveFlow(f); setFlowState(f); }, []);
  useEffect(() => {
    if (status && flow && nextStepIndex(flow, status.steps) === -1) setFlow(null);
  }, [status, flow, setFlow]);
  const markWelcome = () => {
    setStatus((s) => ({ ...s, welcome_seen: true }));
    api.post("/setup/welcome-seen").catch(() => {});
  };
  const startFlow = () => { goal("onboarding_started"); setFlow({ idx: 0 }); };

  if (!eligible || !status) return null;
  const stepIdx = nextStepIndex(flow, status.steps);
  const step = stepIdx >= 0 ? SETUP_STEPS[stepIdx] : null;
  const allDone = SETUP_STEPS.every((s) => status.steps[s.key]);

  return (
    <>
      {!(allDone && hidden) && (
        <SetupCard steps={status.steps} onContinue={startFlow}
                   onHide={() => { lsSet(lsKey(user, "hidden"), "1"); setHidden(true); }} />
      )}
      <SetupPromptDialog open={!status.welcome_seen && !flow} testId="setup-welcome"
        title="Penz’e hoş geldiniz"
        text="Penz’i birkaç adımda kullanmaya başlayabilirsiniz. İsterseniz adımları şimdi tamamlayabilir, isterseniz daha sonra devam edebilirsiniz."
        primary="Başlayalım" onPrimary={() => { markWelcome(); startFlow(); }} onSkip={markWelcome} />
      {step && (
        <SetupPromptDialog open testId={`setup-step-${step.key}`} eyebrow={`Adım ${stepIdx + 1} / ${SETUP_STEPS.length}`}
          title={step.title} text={step.text} primary={step.cta}
          onPrimary={() => { setFlow({ ...flow, idx: stepIdx, awaiting: step.key }); navigate(`${step.path}?new=1`); }}
          onSkip={() => setFlow({ ...flow, idx: stepIdx + 1 })} />
      )}
    </>
  );
}
